import React, { useState, useEffect } from 'react';
import './EAControlPanel.css';
import { useCalendarSync } from '../hooks/useCalendarSync';

const API = 'https://calendar.nareea.co.uk';

function LoadingDots({ text, subtext }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '30px 20px' }}>
      <div style={{ display: 'flex', gap: 8 }}>
        {[0,1,2].map(i => (
          <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: '#1a73e8', animation: `eaDot 1.2s ease-in-out ${i * 0.2}s infinite` }} />
        ))}
      </div>
      <div style={{ fontSize: 13, color: '#5f6368' }}>{text}</div>
      {subtext && <div style={{ fontSize: 11, color: '#9aa0a6' }}>{subtext}</div>}
      <style>{`@keyframes eaDot { 0%,80%,100%{transform:scale(0.7);opacity:0.4} 40%{transform:scale(1.2);opacity:1} }`}</style>
    </div>
  );
}

function hasConflict(event, allEvents) {
  return allEvents.some(other => {
    if (other.uid === event.uid) return false;
    if (other.date !== event.date) return false;
    const s1 = event.startTime.replace(':', '');
    const e1 = event.endTime.replace(':', '');
    const s2 = other.startTime.replace(':', '');
    const e2 = other.endTime.replace(':', '');
    return s1 < e2 && s2 < e1;
  });
}

function getConflictingEvent(event, allEvents) {
  return allEvents.find(other => {
    if (other.uid === event.uid) return false;
    if (other.date !== event.date) return false;
    const s1 = event.startTime.replace(':', '');
    const e1 = event.endTime.replace(':', '');
    const s2 = other.startTime.replace(':', '');
    const e2 = other.endTime.replace(':', '');
    return s1 < e2 && s2 < e1;
  });
}

function getResolutionOptions(event, conflictingEvent) {
  const isBoardOrStrategic = event.type === 'strategic' || (event.title || '').toLowerCase().includes('board');
  const isCall = event.type === 'call' || (event.title || '').toLowerCase().includes('call');
  const conflictIsCall = conflictingEvent && (conflictingEvent.type === 'call' || (conflictingEvent.title || '').toLowerCase().includes('call'));
  const conflictIsBoard = conflictingEvent && ((conflictingEvent.title || '').toLowerCase().includes('board') || conflictingEvent.type === 'strategic');

  if (isBoardOrStrategic && conflictIsCall) {
    return [
      { id: 'brief-board', title: 'Brief Board & Take Call', details: ['Pause presentation briefly', '"Urgent matter, 10min break"', 'Handle call privately', 'Resume with update'], risk: 'Low — Board expects urgent business' },
      { id: 'reschedule-call', title: `Reschedule ${conflictingEvent?.title || 'Call'}`, details: ['Notify caller of conflict', 'Offer alternative slot +2h', 'Ensure full attention later'], risk: 'Medium — caller may be frustrated' },
      { id: 'delegate', title: 'Delegate Call', details: ['Route to COO or deputy', 'Get key details afterwards', 'Schedule proper follow-up'], risk: 'High — caller may want you specifically' },
    ];
  }
  if (isCall && conflictIsBoard) {
    return [
      { id: 'reschedule-call', title: 'Reschedule This Call', details: ['Move call to after board meeting', 'Send updated calendar invite', 'Notify participants'], risk: 'Low — board meeting takes priority' },
      { id: 'brief-board', title: 'Take Call, Brief Board', details: ['Notify board of brief delay', 'Take call first', 'Join board meeting late'], risk: 'Medium — board may be inconvenienced' },
      { id: 'delegate', title: 'Delegate to Deputy', details: ['Route to available colleague', 'Get briefing afterwards'], risk: 'High — may miss key decisions' },
    ];
  }
  return [
    { id: 'reschedule-first', title: `Move: ${(event.title || '').substring(0, 30)}`, details: ['Reschedule to next free slot', 'Notify all participants', 'Send updated invite'], risk: 'Low — standard reschedule' },
    { id: 'reschedule-second', title: `Move: ${(conflictingEvent?.title || 'Other event').substring(0, 30)}`, details: ['Move conflicting event', 'Find alternative time slot', 'Update all attendees'], risk: 'Medium — depends on attendees' },
    { id: 'delegate', title: 'Delegate One Event', details: ['Assign deputy to one event', 'Get summary afterwards', 'Follow up if needed'], risk: 'High — you miss one event' },
  ];
}

function formatAnalysis(text) {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
}

const OFFICE_FLAGS = {
  london: 'LON', new_york: 'NYC', san_francisco: 'SF',
  warsaw: 'WAW', dublin: 'DUB', tokyo: 'TYO',
  seoul: 'SEL', singapore: 'SGP', bangalore: 'BLR',
  sydney: 'SYD', sao_paulo: 'GRU', berlin: 'BER',
  paris: 'CDG', mexico_city: 'MEX',
};

function AIAnalysisModal({ event, conflictingEvent, onClose }) {
  const [analysis, setAnalysis] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/analyze-conflict`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, conflictingEvent, registry: {} }),
    })
      .then(r => r.json())
      .then(data => { setAnalysis(data.analysis || ''); setLoading(false); })
      .catch(() => { setAnalysis('Analysis unavailable.'); setLoading(false); });
  }, [event, conflictingEvent]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 640, width: '100%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 11, color: '#5f6368', fontWeight: 600, letterSpacing: 1, marginBottom: 4 }}>AI ANALYSIS</div>
            <div style={{ fontSize: 16, fontWeight: 500, color: '#1a73e8' }}>{event.title}</div>
            <div style={{ fontSize: 13, color: '#5f6368', marginTop: 2 }}>conflicts with: {conflictingEvent?.title} · {event.date} {event.startTime}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#5f6368', lineHeight: 1 }}>×</button>
        </div>
        {loading ? (
          <LoadingDots text="Claude is analyzing the conflict..." subtext="This may take a few seconds" />
        ) : (
          <div style={{ fontSize: 14, lineHeight: 1.7, color: '#3c4043', background: '#f8f9fa', borderRadius: 8, padding: 16 }}
            dangerouslySetInnerHTML={{ __html: formatAnalysis(analysis) }} />
        )}
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Close</button>
        </div>
      </div>
    </div>
  );
}

function BufferCard({ alert, allEvents }) {
  const [applied, setApplied] = useState(false);
  const [applying, setApplying] = useState(false);

  const handleAddBuffer = async () => {
    setApplying(true);
    try {
      const event1 = allEvents.find(e => e.title === alert.event1);
      const event2 = allEvents.find(e => e.title === alert.event2);
      if (!event1 || !event2) { setApplying(false); return; }
      const res = await fetch(`${API}/api/auto-buffer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event1, event2, requiredBuffer: alert.required_buffer }),
      });
      const data = await res.json();
      if (data.success) setApplied(true);
    } catch (e) { console.error(e); }
    setApplying(false);
  };

  return (
    <div style={{ border: `1px solid ${alert.severity === 'critical' ? '#ea4335' : '#1a73e8'}`, borderRadius: 10, padding: 14, marginBottom: 12, background: alert.severity === 'critical' ? '#fce8e6' : '#e8f0fe' }}>
      <div style={{ fontWeight: 600, fontSize: 13, color: alert.severity === 'critical' ? '#ea4335' : '#1a73e8', marginBottom: 8 }}>
        {alert.severity === 'critical' ? 'Critical' : 'Warning'} — Buffer too short
      </div>
      <div style={{ fontSize: 13, color: '#3c4043', marginBottom: 6 }}>{alert.message}</div>
      <div style={{ fontSize: 12, color: '#5f6368', marginBottom: 10 }}>
        Actual: <strong>{alert.actual_buffer} min</strong> · Required: <strong>{alert.required_buffer} min</strong>
      </div>
      <button onClick={handleAddBuffer} disabled={applied || applying}
        style={{ background: applied ? '#34a853' : applying ? '#dadce0' : '#1a73e8', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: applied || applying ? 'not-allowed' : 'pointer' }}>
        {applied ? 'Buffer Added!' : applying ? 'Applying...' : `Add ${alert.required_buffer - alert.actual_buffer} min Buffer`}
      </button>
    </div>
  );
}

function ReschedulePreviewModal({ event, conflictingEvent, allEvents, onRescheduled, onClose }) {
  const [loading, setLoading] = useState(true);
  const [slots, setSlots] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(null);
  const [horizon, setHorizon] = useState('week');

  const toMove = (conflictingEvent?.priority === 'critical' || conflictingEvent?.type === 'strategic')
    ? event : conflictingEvent;

  const fetchSlots = (h) => {
    setLoading(true);
    setSlots([]);
    setSelectedIdx(0);
    fetch(`${API}/api/smart-reschedule`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: toMove, allEvents, returnSlots: true, horizon: h }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          const primary = { date: data.to.date, startTime: data.to.startTime, endTime: data.to.endTime, allFree: true };
          setSlots([primary, ...(data.alternatives || [])]);
        } else { setSlots([]); }
        setLoading(false);
      })
      .catch(() => { setSlots([]); setLoading(false); });
  };

  useEffect(() => { fetchSlots('week'); }, []);

  const handleConfirm = async () => {
    if (!slots[selectedIdx]) return;
    setConfirming(true);
    try {
      const slot = slots[selectedIdx];
      const res = await fetch(`${API}/api/smart-reschedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event: toMove, allEvents, forceSlot: slot }),
      });
      const data = await res.json();
      if (data.success) {
        setDone(data);
        if (onRescheduled) onRescheduled(data);
      }
    } catch (e) {}
    setConfirming(false);
  };

  if (done) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
        <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 480, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#137333', marginBottom: 12 }}>Event rescheduled</div>
          <div style={{ background: '#e6f4ea', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#137333', marginBottom: 20 }}>
            <strong>{done.event}</strong><br />
            {done.from.date} {done.from.startTime} to {done.to.date} {done.to.startTime}
          </div>
          <button onClick={onClose} style={{ background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer', width: '100%' }}>Close</button>
        </div>
      </div>
    );
  }

  const selected = slots[selectedIdx];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 560, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 11, color: '#5f6368', fontWeight: 600, letterSpacing: 1, marginBottom: 4 }}>RESCHEDULE EVENT</div>
            <div style={{ fontSize: 16, fontWeight: 500, color: '#1a73e8' }}>{toMove?.title}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#5f6368', lineHeight: 1 }}>x</button>
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {[{id:'week',label:'1 Week'},{id:'month',label:'1 Month'},{id:'3months',label:'3 Months'}].map(h => (
            <button key={h.id} onClick={() => { setHorizon(h.id); fetchSlots(h.id); }} style={{ padding: '6px 14px', border: 'none', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: horizon === h.id ? '#1a73e8' : '#f1f3f4', color: horizon === h.id ? '#fff' : '#5f6368' }}>{h.label}</button>
          ))}
          {loading && <span style={{ fontSize: 12, color: '#9aa0a6', alignSelf: 'center', marginLeft: 8 }}>Searching...</span>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
          <div style={{ border: '1px solid #dadce0', borderRadius: 8, padding: 14 }}>
            <div style={{ fontSize: 11, color: '#5f6368', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Before</div>
            <div style={{ background: '#e6f4ea', borderLeft: '3px solid #34a853', borderRadius: 4, padding: '10px 12px' }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#137333', marginBottom: 4 }}>{toMove?.title}</div>
              <div style={{ fontSize: 12, color: '#34a853' }}>{toMove?.date} {toMove?.startTime}-{toMove?.endTime}</div>
            </div>
          </div>
          <div style={{ border: '2px solid #1a73e8', borderRadius: 8, padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: '#5f6368', textTransform: 'uppercase', letterSpacing: 1 }}>After</div>
              {selected && <span style={{ fontSize: 11, background: '#e8f0fe', color: '#1a73e8', padding: '2px 8px', borderRadius: 4 }}>selected</span>}
            </div>
            {loading ? (
              <div style={{ fontSize: 13, color: '#9aa0a6', padding: '10px 0' }}>Finding slots...</div>
            ) : selected ? (
              <div style={{ background: '#e8f0fe', borderLeft: '3px solid #1a73e8', borderRadius: 4, padding: '10px 12px' }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#0c447c', marginBottom: 4 }}>{toMove?.title}</div>
                <div style={{ fontSize: 12, color: '#1a73e8' }}>{selected.date} {selected.startTime}{selected.endTime ? '-'+selected.endTime : ''}</div>
                {selected.allFree && <div style={{ fontSize: 11, color: '#1a73e8', marginTop: 4 }}>all attendees free</div>}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: '#ea4335', padding: '10px 0' }}>No free slots found</div>
            )}
          </div>
        </div>
        {!loading && slots.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, color: '#5f6368', fontWeight: 600, marginBottom: 10 }}>Suggested slots</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {slots.map((slot, i) => (
                <div key={i} onClick={() => setSelectedIdx(i)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 8, border: selectedIdx === i ? '2px solid #1a73e8' : '1px solid #dadce0', background: selectedIdx === i ? '#e8f0fe' : '#fff', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontSize: 14, color: selectedIdx === i ? '#1a73e8' : '#9aa0a6' }}>{selectedIdx === i ? 'v' : 'o'}</div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: selectedIdx === i ? '#0c447c' : '#3c4043' }}>{slot.date} {slot.startTime}{slot.endTime ? '-'+slot.endTime : ''}</div>
                      <div style={{ fontSize: 11, color: selectedIdx === i ? '#1a73e8' : '#5f6368' }}>{slot.allFree ? 'all free' : slot.note || ''}</div>
                    </div>
                  </div>
                  {i === 0 && <span style={{ fontSize: 11, background: '#e8f0fe', color: '#1a73e8', padding: '3px 8px', borderRadius: 4, border: '1px solid #1a73e8' }}>best</span>}
                </div>
              ))}
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', borderTop: '1px solid #dadce0', paddingTop: 16 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #dadce0', background: '#fff', color: '#5f6368', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleConfirm} disabled={!selected || confirming} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: !selected || confirming ? '#dadce0' : '#1a73e8', color: '#fff', fontSize: 13, fontWeight: 600, cursor: !selected || confirming ? 'not-allowed' : 'pointer' }}>
            {confirming ? 'Rescheduling...' : 'Confirm reschedule'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SmartRescheduleButton({ event, conflictingEvent, allEvents, onRescheduled }) {
  const [showModal, setShowModal] = useState(false);
  const [done, setDone] = useState(null);

  if (done) {
    return (
      <div style={{ background: '#e6f4ea', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#137333' }}>
        Rescheduled: <strong>{done.event}</strong><br />
        {done.from.date} {done.from.startTime} to {done.to.date} {done.to.startTime}
      </div>
    );
  }

  return (
    <>
      {showModal && (
        <ReschedulePreviewModal
          event={event}
          conflictingEvent={conflictingEvent}
          allEvents={allEvents}
          onRescheduled={(data) => { setDone(data); setShowModal(false); if (onRescheduled) onRescheduled(data); }}
          onClose={() => setShowModal(false)}
        />
      )}
      <button
        onClick={() => setShowModal(true)}
        style={{ background: '#34a853', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', width: '100%', marginTop: 8 }}
      >
        Reschedule — Preview
      </button>
    </>
  );
}

const EAControlPanel = ({ onResolve, conflictResolved, onCalendarRefresh, onNavigate }) => {
  const [allEvents, setAllEvents] = useState([]);
  const { broadcast } = useCalendarSync();
  const [conflicts, setConflicts] = useState([]);
  const [selectedConflict, setSelectedConflict] = useState(null);
  const [selectedAction, setSelectedAction] = useState('');
  const [executed, setExecuted] = useState(false);
  const [escalated, setEscalated] = useState(false);
  const [copied, setCopied] = useState('');
  const [resolvedList, setResolvedList] = useState([]);
  const [tzAlerts, setTzAlerts] = useState([]);
  const [activeTab, setActiveTab] = useState('conflicts');
  const [insightPeriod, setInsightPeriod] = useState(7);
  const [travelData, setTravelData] = useState(null);
  const [travelApplying, setTravelApplying] = useState(false);
  const [travelApplied, setTravelApplied] = useState(false);
  const [bufferPopup, setBufferPopup] = useState(null);
  const [insightEvents, setInsightEvents] = useState(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/draft/latest`)
      .then(r => r.json())
      .then(data => {
        const events = data.events || [];
        setAllEvents(events);
        const found = events.filter(e => hasConflict(e, events));
        setConflicts(found);
        if (found.length > 0) setSelectedConflict(found[0]);
        setLoading(false);
        return fetch(`${API}/api/timezone-check`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ events }),
        });
      })
      .then(r => r.json())
      .then(data => setTzAlerts(data.alerts || []))
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (activeTab !== 'insights') return;
    // Use draft as primary source (contains all events incl. chat-added)
    // then filter by period on the frontend
    fetch(`${API}/api/draft/latest`)
      .then(r => r.json())
      .then(data => setInsightEvents(data.events || []))
      .catch(() => setInsightEvents([]));
  }, [activeTab, insightPeriod]);

  useEffect(() => {
    if (activeTab !== 'travel') return;
    setTravelData(null);
    fetch(`${API}/api/travel-buffers`, { method: 'POST' })
      .then(r => r.json())
      .then(data => setTravelData(data))
      .catch(() => setTravelData({ error: true }));
  }, [activeTab]);

  const handleApplyTravelBuffers = async () => {
    if (!travelData || !travelData.new_buffers || !travelData.new_buffers.length) return;
    setTravelApplying(true);
    try {
      const res = await fetch(`${API}/api/travel-buffers/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ buffers: travelData.new_buffers })
      });
      const data = await res.json();
      if (data.success) {
        setTravelApplied(true);
        setTravelData(prev => ({ ...prev, new_buffers: [], already_applied: prev.suggested_buffers.length }));
        if (onCalendarRefresh) onCalendarRefresh();
      }
    } finally { setTravelApplying(false); }
  };

  const handleSelectConflict = (conflict) => {
    if (isResolved(conflict.uid)) return;
    setSelectedConflict(conflict);
    setSelectedAction('');
    setExecuted(false);
  };

  const handleExecute = () => {
    if (!selectedConflict) return;
    setExecuted(true);
    setResolvedList(prev => [...prev, selectedConflict.uid]);
    onResolve(selectedAction);
    const remaining = conflicts.filter(c => c.uid !== selectedConflict.uid && !resolvedList.includes(c.uid));
    setTimeout(() => {
      setSelectedConflict(remaining.length > 0 ? remaining[0] : null);
      setSelectedAction('');
      setExecuted(false);
    }, 2000);
  };

  const handleCopy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const isResolved = (uid) => resolvedList.includes(uid);
  const allResolved = conflicts.length > 0 && conflicts.every(c => isResolved(c.uid));
  const conflictingEvent = selectedConflict ? getConflictingEvent(selectedConflict, allEvents) : null;
  const resolutionOptions = selectedConflict ? getResolutionOptions(selectedConflict, conflictingEvent) : [];
  const tzWarnings = tzAlerts.filter(a => a.type === 'timezone');
  const bufferWarnings = tzAlerts.filter(a => a.type === 'buffer');

  if (loading) {
    return (
      <div className="ea-control-panel" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        <LoadingDots text="Loading calendar data..." />
      </div>
    );
  }

  return (
    <div className="ea-control-panel">
      {showAnalysisModal && selectedConflict && conflictingEvent && (
        <AIAnalysisModal event={selectedConflict} conflictingEvent={conflictingEvent} onClose={() => setShowAnalysisModal(false)} />
      )}

      <div className="panel-header">
        <h3>EA Assistant Dashboard</h3>
        <span className="live-indicator">LIVE</span>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid #dadce0', paddingBottom: 12 }}>
        <button onClick={() => setActiveTab('conflicts')} style={{ flex: 1, padding: '8px', border: 'none', borderRadius: 8, background: activeTab === 'conflicts' ? '#fce8e6' : '#f1f3f4', color: activeTab === 'conflicts' ? '#ea4335' : '#5f6368', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
          Conflicts ({conflicts.filter(c => !isResolved(c.uid)).length})
        </button>
        <button onClick={() => setActiveTab('timezone')} style={{ flex: 1, padding: '8px', border: 'none', borderRadius: 8, background: activeTab === 'timezone' ? '#fef7e0' : '#f1f3f4', color: activeTab === 'timezone' ? '#b06000' : '#5f6368', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
          Timezone ({tzWarnings.length})
        </button>
        <button onClick={() => setActiveTab('buffer')} style={{ flex: 1, padding: '8px', border: 'none', borderRadius: 8, background: activeTab === 'buffer' ? '#e8f0fe' : '#f1f3f4', color: activeTab === 'buffer' ? '#1a73e8' : '#5f6368', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
          Buffer ({bufferWarnings.length})
        </button>
        <button onClick={() => setActiveTab('insights')} style={{ flex: 1, padding: '8px', border: 'none', borderRadius: 8, background: activeTab === 'insights' ? '#e6f4ea' : '#f1f3f4', color: activeTab === 'insights' ? '#137333' : '#5f6368', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
          📊 Insights
        </button>
        <button onClick={() => setActiveTab('travel')} style={{ flex: 1, padding: '8px', border: 'none', borderRadius: 8, background: activeTab === 'travel' ? '#fce8e6' : '#f1f3f4', color: activeTab === 'travel' ? '#c5221f' : '#5f6368', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
          ✈ Travel
        </button>
      </div>

      {activeTab === 'conflicts' && (
        <>
          {conflicts.length === 0 && <div className="no-conflicts">No conflicts detected</div>}
          {conflicts.length > 0 && (
            <div className="conflicts-list">
              <div className="conflicts-title">Conflicts ({conflicts.filter(c => !isResolved(c.uid)).length} remaining)</div>
              {conflicts.map((c, i) => (
                <div key={i} className={`conflict-item ${selectedConflict?.uid === c.uid ? 'active' : ''} ${isResolved(c.uid) ? 'resolved' : ''}`} onClick={() => handleSelectConflict(c)}>
                  <span className="conflict-dot">{isResolved(c.uid) ? '✓' : '!'}</span>
                  <div className="conflict-item-info">
                    <span className="conflict-name">{c.title}</span>
                    <span className="conflict-time">{c.date} · {c.startTime}-{c.endTime}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {selectedConflict && !isResolved(selectedConflict.uid) && (
            <>
              <div className="active-conflict">
                <div className="conflict-title">Active Conflict — {selectedConflict.date} {selectedConflict.startTime}</div>
                <div className="conflict-details">
                  <p><strong>Event:</strong> {selectedConflict.title}</p>
                  <p><strong>Time:</strong> {selectedConflict.startTime} — {selectedConflict.endTime}</p>
                  {conflictingEvent && <p><strong>Conflicts with:</strong> {conflictingEvent.title} ({conflictingEvent.startTime})</p>}
                  <p><strong>Priority:</strong> {selectedConflict.priority}</p>
                </div>
                <button onClick={() => setShowAnalysisModal(true)}
                  style={{ marginTop: 10, background: '#e8f0fe', color: '#1a73e8', border: '1px solid #1a73e8', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', width: '100%' }}>
                  View AI Analysis
                </button>
                <SmartRescheduleButton
                  event={selectedConflict}
                  conflictingEvent={conflictingEvent}
                  allEvents={allEvents}
                  onRescheduled={(data) => { setResolvedList(prev => [...prev, selectedConflict.uid]);
                  broadcast('EVENT_RESCHEDULED', data);
                  if (onCalendarRefresh) onCalendarRefresh();
                 }}
                />
              </div>

              <div className="decision-panel">
                <h4>Resolution Options:</h4>
                {resolutionOptions.map((opt) => (
                  <div key={opt.id} className="option-card" onClick={() => setSelectedAction(opt.id)}>
                    <input type="radio" name="resolution" value={opt.id} checked={selectedAction === opt.id} readOnly />
                    <div className="option-content">
                      <div className="option-title">{opt.title}</div>
                      <div className="option-details">{opt.details.map((d, i) => <span key={i}>• {d}<br /></span>)}</div>
                      <div className="option-risk">Risk: {opt.risk}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="action-buttons">
                <button className="execute-btn" disabled={!selectedAction} onClick={handleExecute}>Execute Decision</button>
                <button className="escalate-btn" onClick={() => { setEscalated(true); setTimeout(() => setEscalated(false), 3000); }}>Escalate to Luke</button>
              </div>

              {executed && <div className="action-feedback success">Decision executed — moving to next conflict</div>}
              {escalated && <div className="action-feedback warning">Escalated to Luke — awaiting input</div>}
            </>
          )}
          {allResolved && <div className="action-feedback success" style={{ marginTop: 16 }}>All {conflicts.length} conflicts resolved!</div>}
        </>
      )}

      {activeTab === 'timezone' && (
        <div>
          {tzWarnings.length === 0 && <div className="no-conflicts">No timezone issues detected</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {tzWarnings.map((alert, i) => (
              <div key={i} style={{ border: '1px solid #fbbc04', borderRadius: 10, padding: 14, background: '#fef7e0' }}>
                <div style={{ fontWeight: 600, fontSize: 12, color: '#b06000', marginBottom: 4 }}>{alert.date}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#3c4043', marginBottom: 10 }}>{alert.event}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {alert.inconvenient_offices.map((office, j) => {
                    const [name, time] = office.split(': ');
                    const hours = parseInt(time.split(':')[0]);
                    const isNight = hours < 6 || hours >= 22;
                    return (
                      <div key={j} style={{ background: isNight ? '#ea4335' : '#fa7b17', color: '#fff', borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 600 }}>
                        {OFFICE_FLAGS[name] || name.toUpperCase()} {time}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'buffer' && (
        <div>
          {bufferWarnings.length === 0 && <div className="no-conflicts">No buffer issues detected</div>}
          {bufferWarnings.map((alert, i) => (
            <BufferCard key={i} alert={alert} allEvents={allEvents} />
          ))}
        </div>
      )}


      {activeTab === 'insights' && (() => {
        // Compute insights from allEvents
        const now = new Date();
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay() + 1);
        startOfWeek.setHours(0,0,0,0);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        endOfWeek.setHours(23,59,59,999);

        const startNextWeek = new Date(endOfWeek);
        startNextWeek.setDate(endOfWeek.getDate() + 1);
        startNextWeek.setHours(0,0,0,0);
        const endNextWeek = new Date(startNextWeek);
        endNextWeek.setDate(startNextWeek.getDate() + 6);
        endNextWeek.setHours(23,59,59,999);

        const periodStart = new Date(startOfWeek);
        const periodEnd = new Date(startOfWeek);
        periodEnd.setDate(startOfWeek.getDate() + insightPeriod);
        const eventsForInsight = (insightEvents || allEvents).filter(e => {
          const d = new Date(e.date);
          return d >= periodStart && d <= periodEnd;
        });
        const thisWeekEvents = eventsForInsight.filter(e => {
          const d = new Date(e.date);
          return d >= startOfWeek && d <= endOfWeek;
        });
        const nextWeekEvents = eventsForInsight.filter(e => {
          const d = new Date(e.date);
          return d >= startNextWeek && d <= endNextWeek;
        });

        // Events by day of week (this week)
        const byDay = { Mon:0, Tue:0, Wed:0, Thu:0, Fri:0 };
        const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
        thisWeekEvents.forEach(e => {
          const d = new Date(e.date);
          const name = dayNames[d.getDay()];
          if (byDay[name] !== undefined) byDay[name]++;
        });
        const maxDay = Object.entries(byDay).sort((a,b) => b[1]-a[1])[0];
        const minDay = Object.entries(byDay).filter(([,v])=>v>0).sort((a,b) => a[1]-b[1])[0];

        // Top participants
        const participantCount = {};
        eventsForInsight.forEach(e => {
          (e.attendees || []).forEach(p => {
            const name = typeof p === 'string' ? p : (p.name || p.email || '');
            if (name && !name.toLowerCase().includes('luke') && !name.toLowerCase().includes('harries')) {
              participantCount[name] = (participantCount[name] || 0) + 1;
            }
          });
        });
        const topParticipants = Object.entries(participantCount).sort((a,b)=>b[1]-a[1]).slice(0,5);

        // Recurring titles
        const titleCount = {};
        eventsForInsight.forEach(e => {
          const t = (e.title || '').trim();
          if (t) titleCount[t] = (titleCount[t] || 0) + 1;
        });
        const topMeetings = Object.entries(titleCount).sort((a,b)=>b[1]-a[1]).slice(0,4);

        const nextWeekLoad = nextWeekEvents.length;
        const loadLevel = nextWeekLoad >= 10 ? 'high' : nextWeekLoad >= 6 ? 'medium' : 'normal';
        const loadColor = loadLevel === 'high' ? '#ea4335' : loadLevel === 'medium' ? '#fa7b17' : '#34a853';
        const loadBg = loadLevel === 'high' ? '#fce8e6' : loadLevel === 'medium' ? '#fef3e2' : '#e6f4ea';

        const statBox = (label, value, sub, color='#1a73e8', bg='#e8f0fe') => (
          <div style={{ background: bg, borderRadius: 10, padding: '12px 16px', flex: 1, minWidth: 120 }}>
            <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#3c4043', marginTop: 2 }}>{label}</div>
            {sub && <div style={{ fontSize: 11, color: '#5f6368', marginTop: 2 }}>{sub}</div>}
          </div>
        );

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {[{label:'Week',days:7},{label:'Month',days:30},{label:'3 Months',days:90}].map(p => (
                <button key={p.days} onClick={() => setInsightPeriod(p.days)}
                  style={{ padding:'6px 16px', border:'none', borderRadius:20, fontSize:12, fontWeight:600, cursor:'pointer',
                    background: insightPeriod===p.days ? '#1a73e8' : '#f1f3f4',
                    color: insightPeriod===p.days ? '#fff' : '#5f6368' }}>
                  {p.label}
                </button>
              ))}
              {!insightEvents && <span style={{fontSize:12,color:'#9aa0a6',alignSelf:'center'}}>Loading...</span>}
            </div>

            {/* Stats row */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {statBox(insightPeriod===7?'This week':insightPeriod===30?'This month':'3 months', eventsForInsight.length, 'events total', '#1a73e8', '#e8f0fe')}
              {statBox('Next week', nextWeekEvents.length, `${loadLevel} load`, loadColor, loadBg)}
              {maxDay && statBox('Busiest day', maxDay[0], `${maxDay[1]} events`, '#ea4335', '#fce8e6')}
              {minDay && statBox('Lightest day', minDay[0], `${minDay[1]} event${minDay[1]!==1?'s':''}`, '#34a853', '#e6f4ea')}
            </div>

            {/* Next week warning */}
            {loadLevel !== 'normal' && (
              <div style={{ background: loadBg, border: `1px solid ${loadColor}`, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 20 }}>{loadLevel === 'high' ? '⚠️' : '📅'}</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: loadColor }}>
                    Next week: {nextWeekLoad} events — {loadLevel === 'high' ? 'HIGH load' : 'moderate load'}
                  </div>
                  <div style={{ fontSize: 12, color: '#5f6368', marginTop: 2 }}>
                    {loadLevel === 'high' ? 'Consider blocking focus time or rescheduling lower-priority meetings' : 'Manageable but watch for conflicts'}
                  </div>
                </div>
              </div>
            )}

            {/* Load by day bar chart */}
            <div style={{ background: '#f8f9fa', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#3c4043', marginBottom: 12 }}>THIS WEEK — LOAD BY DAY</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', height: 64 }}>
                {Object.entries(byDay).map(([day, count]) => {
                  const maxCount = Math.max(...Object.values(byDay), 1);
                  const h = count === 0 ? 4 : Math.max(8, (count / maxCount) * 56);
                  const isMax = count === maxCount && count > 0;
                  return (
                    <div key={day} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <div style={{ fontSize: 11, color: '#5f6368', fontWeight: 600 }}>{count}</div>
                      <div style={{ width: '100%', height: h, background: isMax ? '#ea4335' : '#1a73e8', borderRadius: 4, opacity: count===0 ? 0.2 : 1, transition: 'height 0.3s' }} />
                      <div style={{ fontSize: 11, color: isMax ? '#ea4335' : '#5f6368', fontWeight: isMax ? 700 : 400 }}>{day}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Top meetings */}
            {topMeetings.length > 0 && (
              <div style={{ background: '#f8f9fa', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#3c4043', marginBottom: 10 }}>RECURRING MEETINGS</div>
                {topMeetings.map(([title, count], i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: i < topMeetings.length-1 ? '1px solid #e8eaed' : 'none' }}>
                    <div style={{ fontSize: 13, color: '#3c4043', flex: 1, marginRight: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#1a73e8', background: '#e8f0fe', borderRadius: 12, padding: '2px 8px', whiteSpace: 'nowrap' }}>{count}×</div>
                  </div>
                ))}
              </div>
            )}

            {/* Top participants */}
            {topParticipants.length > 0 && (
              <div style={{ background: '#f8f9fa', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#3c4043', marginBottom: 10 }}>TOP MEETING PARTICIPANTS</div>
                {topParticipants.map(([name, count], i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: i < topParticipants.length-1 ? '1px solid #e8eaed' : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: `hsl(${(i*67+200)%360},60%,55%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', fontWeight: 700 }}>
                        {name.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
                      </div>
                      <div style={{ fontSize: 13, color: '#3c4043' }}>{name}</div>
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#34a853', background: '#e6f4ea', borderRadius: 12, padding: '2px 8px' }}>{count} meetings</div>
                  </div>
                ))}
              </div>
            )}

            {topParticipants.length === 0 && topMeetings.length === 0 && (
              <div style={{ textAlign: 'center', color: '#9aa0a6', padding: '20px', fontSize: 13 }}>
                Sync Google Calendar to see insights
              </div>
            )}
          </div>
        );

      })()}

      {activeTab === 'travel' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {!travelData && (
            <div style={{ textAlign: 'center', color: '#9aa0a6', padding: 30 }}>
              Scanning calendar for travel events...
            </div>
          )}
          {travelData && travelData.error && (
            <div style={{ color: '#ea4335', padding: 20 }}>Failed to load travel data</div>
          )}
          {travelData && !travelData.error && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ background: '#f8f9fa', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#3c4043', marginBottom: 10 }}>
                  TRAVEL EVENTS DETECTED ({(travelData.travel_events || []).length})
                </div>
                {(travelData.travel_events || []).length === 0 && (
                  <div style={{ fontSize: 13, color: '#9aa0a6' }}>No travel events found in calendar</div>
                )}
                {(travelData.travel_events || []).map((e, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < travelData.travel_events.length - 1 ? '1px solid #e8eaed' : 'none' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#3c4043' }}>{e.title}</div>
                      <div style={{ fontSize: 11, color: '#5f6368' }}>{e.date} · {e.startTime}–{e.endTime}</div>
                    </div>
                    <span onClick={(ev) => { ev.stopPropagation(); if(onNavigate) onNavigate('calendar', e.date); if(onNavigate) onNavigate('calendar', e.date); else console.log('onNavigate is null'); }} style={{ fontSize: 11, background: '#fce8e6', color: '#c5221f', borderRadius: 6, padding: '3px 8px', fontWeight: 600, cursor: 'pointer' }}>✈ FLIGHT</span>
                  </div>
                ))}
              </div>

              <div style={{ background: '#f8f9fa', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#3c4043', marginBottom: 10 }}>
                  SUGGESTED BUFFERS ({(travelData.suggested_buffers || []).length})
                </div>
                {(travelData.suggested_buffers || []).map((b, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < travelData.suggested_buffers.length - 1 ? '1px solid #e8eaed' : 'none' }}>
                    <div>
                      <div style={{ fontSize: 13, color: '#3c4043' }}>{b.title}</div>
                      <div style={{ fontSize: 11, color: '#5f6368' }}>{b.date} · {b.startTime}–{b.endTime}</div>
                    </div>
                    {(travelData.new_buffers || []).some(n => n.uid === b.uid)
                      ? <div style={{ fontSize: 11, background: '#fef7e0', color: '#b06000', borderRadius: 6, padding: '3px 8px', fontWeight: 600 }}>NEW</div>
                      : <div onClick={(ev) => { ev.stopPropagation(); console.log('bufferPopup data:', b); setBufferPopup({...b}); }} style={{ fontSize: 11, background: '#e6f4ea', color: '#137333', borderRadius: 6, padding: '3px 8px', fontWeight: 600, cursor: 'pointer' }}>APPLIED ℹ</div>
                    }
                  </div>
                ))}
              </div>

              {(travelData.new_buffers || []).length > 0 && (
                <button onClick={handleApplyTravelBuffers} disabled={travelApplying}
                  style={{ background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 20px', fontSize: 14, fontWeight: 700, cursor: travelApplying ? 'wait' : 'pointer', width: '100%' }}>
                  {travelApplying ? 'Applying...' : 'Apply ' + travelData.new_buffers.length + ' buffer' + (travelData.new_buffers.length !== 1 ? 's' : '') + ' to Calendar'}
                </button>
              )}
              {travelApplied && (
                <div style={{ background: '#e6f4ea', color: '#137333', borderRadius: 10, padding: '12px 16px', fontSize: 13, fontWeight: 600, textAlign: 'center' }}>
                  Travel buffers applied — calendar updated
                </div>
              )}
              {(travelData.new_buffers || []).length === 0 && !travelApplied && (travelData.travel_events || []).length > 0 && (
                <div style={{ background: '#e6f4ea', color: '#137333', borderRadius: 10, padding: '12px 16px', fontSize: 13, fontWeight: 600, textAlign: 'center' }}>
                  All travel buffers already applied
                </div>
              )}
            </div>
          )}
        </div>
      )}


      {bufferPopup && (
        <div onClick={() => setBufferPopup(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={(ev) => ev.stopPropagation()} style={{ background: '#fff', borderRadius: 16, padding: 28, maxWidth: 400, width: '90%', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#3c4043', marginBottom: 16 }}>{bufferPopup.title}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e8eaed' }}>
                <span style={{ fontSize: 13, color: '#5f6368' }}>Date</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#3c4043' }}>{bufferPopup.date}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e8eaed' }}>
                <span style={{ fontSize: 13, color: '#5f6368' }}>Time</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#3c4043' }}>{bufferPopup.startTime} – {bufferPopup.endTime}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e8eaed' }}>
                <span style={{ fontSize: 13, color: '#5f6368' }}>Priority</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#ea4335' }}>{bufferPopup.priority}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                <span style={{ fontSize: 13, color: '#5f6368' }}>Status</span>
                <span style={{ fontSize: 13, fontWeight: 600, background: '#e6f4ea', color: '#137333', borderRadius: 6, padding: '2px 8px' }}>APPLIED</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button onClick={() => setBufferPopup(null)} style={{ flex: 1, padding: '10px', background: '#f1f3f4', color: '#3c4043', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Close</button>
              <button onClick={() => { if(onNavigate) onNavigate('calendar', bufferPopup.date); setBufferPopup(null); }} style={{ flex: 2, padding: '10px', background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>View in Calendar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EAControlPanel;

